/**
 * Author: Claude Code
 * Version: v3.0.80
 * Modified: 2025-10-26 Beijing Time // Resource sprites scale=50% tile; roundPixels by camera
 * 
 * ResourceSystem - 前端资源可视化管理系统
 * 桥接后端ResourceManager，处理武器和医疗资源的视觉呈现
 */

import Phaser from 'phaser';

export interface ResourceData {
  itemId: string;
  type: 'WEAPON' | 'CONSUMABLE';
  category: 'weapon' | 'medical';
  weaponType?: 'SWORD' | 'AXE' | 'LANCE';
  medicalType?: 'large' | 'small';
  position: { x: number; y: number };
}

export interface ResourceSprite extends Phaser.GameObjects.Sprite {
  resourceData: ResourceData;
}

export class ResourceSystem {
  private scene: Phaser.Scene;
  private resources: Map<string, ResourceSprite> = new Map();
  private resourceGroup: Phaser.GameObjects.Group;
  private tileSize: number;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.resourceGroup = scene.add.group();
    // 统一以 Scene 的 gridSize 为基准，回退 48（与地图一致）
    const byProp = (scene as any)?.gridSize;
    const byRegistry = (scene.registry && typeof (scene.registry as any).get === 'function')
      ? ((scene.registry as any).get('gridSize') || (scene.registry as any).get('tileSize'))
      : undefined;
    this.tileSize = (typeof byProp === 'number' && byProp > 0)
      ? byProp
      : (typeof byRegistry === 'number' && byRegistry > 0 ? byRegistry : 48);
  }

  // Resource spawn management
  spawnResource(resourceData: ResourceData): void {
    let textureKey: string;
    
    if (resourceData.category === 'weapon' && resourceData.weaponType) {
      textureKey = this.getWeaponResourceKey(resourceData.weaponType);
    } else if (resourceData.category === 'medical' && resourceData.medicalType) {
      textureKey = this.getMedicalResourceKey(resourceData.medicalType);
    } else {
      textureKey = 'resource-default';
    }

    // 统一使用 Scene.gridSize 做 grid→pixel 转换
    const px = resourceData.position.x * this.tileSize + this.tileSize / 2;
    const py = resourceData.position.y * this.tileSize + this.tileSize / 2;
    const sprite = this.scene.add.sprite(px, py, textureKey) as ResourceSprite;

    sprite.setDepth(5);
    sprite.setOrigin(0.5, 0.5);
    // 无损缩放策略：按类别缩放（medical:70%，weapon:80%；其余:50%），优先整数倍；否则最近邻+roundPixels
    try {
      // 目标：边长=tile的 X%（按最大边对齐，保持比例）
      const ratioByCategory = resourceData.category === 'medical' ? 0.7
        : (resourceData.category === 'weapon' ? 0.8 : 0.5);
      const TARGET_EDGE = this.tileSize * ratioByCategory;
      const tex = sprite.texture;
      const baseW = tex.getSourceImage().width;
      const baseH = tex.getSourceImage().height;
      const baseMax = Math.max(baseW, baseH);
      const ratioExact = TARGET_EDGE / baseMax;
      if (ratioExact >= 1) {
        // 放大：优先取不超目标边长的整数倍，保证像素无损
        const k = Math.max(1, Math.floor(ratioExact));
        sprite.setScale(k);
      } else {
        // 缩小：最近邻比例缩放，像素不糊
        sprite.setScale(ratioExact);
      }
    } catch {
      // 回退：按类别的简化比例
      const fallback = resourceData.category === 'medical' ? 0.7
        : (resourceData.category === 'weapon' ? 0.8 : 0.5);
      sprite.setScale(fallback);
    }
    
    // Store resource data with sprite
    sprite.resourceData = { ...resourceData };
    
    this.resources.set(resourceData.itemId, sprite);
    this.resourceGroup.add(sprite);
  }

  removeResource(itemId: string): void {
    const sprite = this.resources.get(itemId);
    if (sprite) {
      this.resourceGroup.remove(sprite);
      sprite.destroy();
      this.resources.delete(itemId);
    }
  }

  getResource(itemId: string): ResourceData | undefined {
    const sprite = this.resources.get(itemId);
    return sprite?.resourceData;
  }

  // Resource key mapping methods
  getWeaponResourceKey(weaponType: string): string {
    const lowerType = weaponType.toLowerCase();
    return `resource-${lowerType}`;
  }

  getMedicalResourceKey(medicalType: string): string {
    return `resource-${medicalType}-medkit`;
  }

  // WebSocket event handlers
  handleResourceSpawned(eventData: { newItems: ResourceData[] }): void {
    eventData.newItems.forEach(item => {
      this.spawnResource(item);
    });
  }

  handleResourceCollected(eventData: { itemId: string; agentId: string; item: any }): void {
    // 视觉移除改由单一路径（Engine → SceneAdapter）控制，以保证“先移动，后收集”顺序
    // 此处仅更新内部状态或日志，不直接移除sprite
    const sprite = this.resources.get(eventData.itemId);
    if (sprite) {
      // 可选：标记为待移除，避免二次处理
      (sprite as any).__pendingRemoval = true;
    }
    // console.log(`[ResourceSystem] defer visual removal for ${eventData.itemId} by ${eventData.agentId}`);
  }

  handleWeaponEquipped(eventData: { agentId: string; weapon: any }): void {
    // Visual feedback for weapon equipping - can be expanded later
    console.log(`Agent ${eventData.agentId} equipped weapon:`, eventData.weapon);
  }

  handleItemUsed(eventData: { agentId: string; item: any; effect?: any }): void {
    // Visual feedback for item usage - can be expanded later
    console.log(`Agent ${eventData.agentId} used item:`, eventData.item);
  }

  handleWeaponBroken(eventData: { agentId: string; weapon: any }): void {
    // Visual feedback for weapon breaking - can be expanded later
    console.log(`Agent ${eventData.agentId} weapon broken:`, eventData.weapon);
  }

  // Batch operations
  batchSpawnResources(resources: ResourceData[]): void {
    resources.forEach(resource => {
      this.spawnResource(resource);
    });
  }

  getAllResources(): ResourceData[] {
    const allResources: ResourceData[] = [];
    this.resources.forEach((sprite) => {
      allResources.push(sprite.resourceData);
    });
    return allResources;
  }

  // System cleanup
  cleanup(): void {
    this.resources.forEach((sprite, itemId) => {
      this.removeResource(itemId);
    });
    this.resources.clear();
  }
}
